import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { LOCALE_TAG, normalizeLanguage } from "../../supabase/functions/_shared/i18n/locale"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * `lang` is the CLIENT's communication language (en | es | fr) on a
 * client-facing surface; omitted, the contractor-facing default stays en-US.
 */
export function formatCurrency(amount: number, currency = 'USD', lang?: string | null) {
  return new Intl.NumberFormat(lang ? LOCALE_TAG[normalizeLanguage(lang)] : 'en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

/**
 * Money in its short form: whole amounts drop the cents ("$25", "$1,870"),
 * anything else keeps two decimals ("$3.50", "$79.99"). For rate labels such as
 * "$25/ft" and plan prices, where "$25.00/ft" reads as noise. Everything else
 * uses formatCurrency().
 */
export function formatCurrencyCompact(amount: number, currency = 'USD') {
  const whole = Number.isInteger(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(amount);
}

/**
 * Parse a date-only string ('YYYY-MM-DD', e.g. a Postgres `date` column) as
 * LOCAL midnight. `new Date('2026-06-30')` parses as UTC midnight, which renders
 * as the PREVIOUS calendar day in any negative-UTC timezone (all of the US) —
 * wrong for deadlines like a deposit due date or quote validity. Full
 * timestamps (with a time component) pass through to the native parser, which
 * correctly converts an instant-in-time to local.
 */
export function parseDateOnly(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(value);
}

/**
 * The inverse of `parseDateOnly`: a Date rendered as the 'YYYY-MM-DD' its LOCAL
 * calendar sits on. `toISOString().slice(0, 10)` converts to UTC first, which
 * in every US timezone reads as TOMORROW from late afternoon onwards — so a
 * `min` or a "not in the past" check built that way locks the client out of
 * their own today for the last hours of every day.
 *
 * Use for anything a person compares against a `<input type="date">` value:
 * those are local calendar dates, never instants.
 */
export function formatDateOnly(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * A date-only value ('YYYY-MM-DD', a Postgres `date`) rendered for a person:
 * the stored calendar day, in every viewer timezone. `new Date(value)
 * .toLocaleDateString()` reads the value as UTC midnight and so prints the day
 * BEFORE everywhere west of UTC. Empty or unparseable input gives ''. Full
 * timestamps still render in the viewer's zone (parseDateOnly passes them on).
 */
export function formatDateOnlyDisplay(
  value: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  if (!value) return '';
  const date = parseDateOnly(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, options);
}

/**
 * True when a date-only value (a due date, an expiry, a warranty end) is a
 * calendar day BEFORE the viewer's local today — i.e. overdue / expired. The
 * day itself is not overdue. Compares calendar keys, so it does not depend on
 * the hour or on how far the viewer sits from UTC.
 */
export function isDateOnlyBeforeToday(value: string | null | undefined, now: Date = new Date()): boolean {
  if (!value) return false;
  const date = parseDateOnly(value);
  if (Number.isNaN(date.getTime())) return false;
  return formatDateOnly(date) < formatDateOnly(now);
}

/**
 * `formatDateOnly`'s counterpart on the UTC calendar.
 *
 * For the one job a local date cannot do: agreeing with Postgres `current_date`,
 * which is UTC. A bound the SERVER enforces to the day and grants no slack on
 * has to be mirrored in the server's own calendar, or a client whose local day
 * is already UTC-tomorrow is offered a value the RPC refuses.
 *
 * Anything a person reads as "today" still belongs in `formatDateOnly` — this
 * is for reproducing a server-side date rule, not for showing dates to people.
 */
export function formatDateOnlyUTC(date: Date = new Date()): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
